import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { direction } from "@/lib/direction";

interface IAssistantChatGroupLabelProps {
  label: string;
}

// NOTE: NSAI-6754 — an assistant's name can be longer than the sidebar is
// wide. It truncates with an ellipsis, and the tooltip carries the full name,
// matching how chat titles and folder names expose theirs.
const AssistantChatGroupLabelComponent = ({
  label,
}: IAssistantChatGroupLabelProps): JSX.Element => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [isTriggerFocused, setIsTriggerFocused] = useState(false);
  // NOTE: NSAI-6754 — the name is free-form user text, so it truncates in its
  // OWN direction, not the UI locale's: an English name under an Arabic UI (or
  // the reverse) would otherwise lose its first characters instead of its last,
  // the bug NSAI-5893 fixed for chat titles in `ChatListItem`.
  const labelDirection = useMemo(() => direction(label), [label]);

  // HACK: the focusable element is the accordion button around this label,
  // not the label itself. Making that button the tooltip trigger would let the
  // tooltip's `data-state` override the accordion's and break the chevron, so
  // the tooltip also opens while the button has keyboard focus. Mouse clicks
  // focus the button too, but not as `:focus-visible`, so they don't open it.
  useEffect(() => {
    const accordionTrigger = labelRef.current?.closest("button");
    if (!accordionTrigger) {
      return undefined;
    }
    const handleFocus = (): void => {
      setIsTriggerFocused(accordionTrigger.matches(":focus-visible"));
    };
    const handleBlur = (): void => {
      setIsTriggerFocused(false);
    };
    accordionTrigger.addEventListener("focus", handleFocus);
    accordionTrigger.addEventListener("blur", handleBlur);
    return (): void => {
      accordionTrigger.removeEventListener("focus", handleFocus);
      accordionTrigger.removeEventListener("blur", handleBlur);
    };
  }, []);

  // NOTE: Radix closes the tooltip on Escape or pointer leave; either one also
  // dismisses a focus-opened tooltip until the button is focused again.
  const handleOpenChange = useCallback((isOpen: boolean): void => {
    setIsHoverOpen(isOpen);
    if (!isOpen) {
      setIsTriggerFocused(false);
    }
  }, []);

  return (
    <TooltipProvider>
      <Tooltip
        open={isHoverOpen || isTriggerFocused}
        onOpenChange={handleOpenChange}
      >
        <TooltipTrigger asChild>
          <span
            ref={labelRef}
            dir={labelDirection}
            className="min-w-0 truncate text-start"
            data-testid="sidebar-assistant-chat-group-label"
          >
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent
          className="max-w-sm break-words"
          data-testid="sidebar-assistant-chat-group-label-tooltip"
        >
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const AssistantChatGroupLabel = memo(AssistantChatGroupLabelComponent);
